import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await axios.get(`${API}/blog/posts`);
                setPosts(response.data);
            } catch (error) {
                console.error('Failed to fetch blog posts:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPosts();
    }, []);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title="Blog"
                description="Explore articles on hypnotherapy, personal transformation, and the science of the subconscious mind. Tips and insights from Eric Paris."
                canonical="https://hypnosismasteryseries.com/blog"
            />
            {/* Header */}
            <section className="max-w-4xl mx-auto px-6 py-16 text-center">
                <span className="inline-block px-4 py-2 bg-copper-500/10 text-copper-600 rounded-full text-sm font-body font-medium mb-6 opacity-0 animate-fade-in-up">
                    Insights & Guidance
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl font-light text-nature-900 leading-tight mb-6 opacity-0 animate-fade-in-up stagger-1">
                    The <span className="italic">Blog</span>
                </h1>
                <p className="text-lg text-nature-800/80 font-body leading-relaxed max-w-2xl mx-auto opacity-0 animate-fade-in-up stagger-2">
                    Explore articles on hypnotherapy, personal transformation, and the science of the subconscious mind.
                </p>
            </section>

            {/* Blog Posts */}
            <section className="max-w-4xl mx-auto px-6 pb-24">
                {loading ? (
                    <div className="space-y-8">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl p-8 shadow-soft">
                                <div className="h-6 bg-nature-100 rounded w-3/4 mb-4 skeleton" />
                                <div className="h-4 bg-nature-100 rounded w-full mb-2 skeleton" />
                                <div className="h-4 bg-nature-100 rounded w-2/3 skeleton" />
                            </div>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-16">
                        <BookOpen className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                        <h3 className="font-heading text-xl text-nature-900 mb-2">
                            No posts yet
                        </h3>
                        <p className="text-nature-800/60 font-body">
                            Check back soon for new articles.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {posts.map((post, index) => (
                            <article 
                                key={post.id} 
                                className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-md transition-shadow opacity-0 animate-fade-in-up"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="flex items-center gap-2 text-nature-800/50 text-sm font-body mb-3">
                                    <Calendar className="w-4 h-4" />
                                    {formatDate(post.created_at)}
                                </div>
                                
                                <Link to={`/blog/${post.slug}`}>
                                    <h2 className="font-heading text-2xl text-nature-900 mb-3 hover:text-copper-600 transition-colors">
                                        {post.title}
                                    </h2>
                                </Link>
                                
                                <p className="text-nature-800/70 font-body leading-relaxed mb-4">
                                    {post.excerpt}
                                </p>
                                
                                <Link 
                                    to={`/blog/${post.slug}`}
                                    className="inline-flex items-center text-copper-600 font-body font-medium hover:text-copper-700 transition-colors"
                                >
                                    Read More
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Blog;
